import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { kpiTiles, type KpiTile, type KpiTileData } from '../utils/kpiTiles';

interface CompanyKeyFiguresProps extends KpiTileData {
  /** The selected KPI ids (admin defaults or the user's "Anpassa"), in order. */
  kpiIds: string[];
  onCreateProject: () => void;
}

/**
 * "Dina nyckeltal" for a construction company: the selected KPIs as tiles in
 * one look. The numbers share one colour; only the icon hints at the status.
 */
export default function CompanyKeyFiguresPanel({ kpiIds, onCreateProject, ...data }: CompanyKeyFiguresProps) {
  const { t, i18n } = useTranslation();
  // Tiles keep the selected order. A small tile left alone on its row right
  // before a full-width tile takes the full width too, so no gap is left.
  const tiles = kpiTiles(kpiIds, data, t, i18n.language).map((tile, index, all) => {
    if (tile.wide || !all[index + 1]?.wide) return tile;
    let smallBefore = 0;
    for (let i = index - 1; i >= 0 && !all[i].wide; i -= 1) smallBefore += 1;
    return smallBefore % 2 === 0 ? { ...tile, wide: true } : tile;
  });
  if (tiles.length === 0) {
    return <p className="text-center text-muted small py-4 mb-0">{t('home.kpis.empty')}</p>;
  }
  return <KeyFigureList tiles={tiles} onCreateProject={onCreateProject} />;
}

/**
 * The "Dina nyckeltal" tiles themselves, shared by the company start page and
 * the lawyer's and inspector's start pages.
 */
export function KeyFigureList({ tiles, onCreateProject, testId = 'company-key-figures' }: {
  tiles: KpiTile[];
  onCreateProject?: () => void;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <dl className="company-key-figures" data-testid={testId}>
      {tiles.map(tile => (
        <div key={tile.id} className={`company-key-figure${tile.wide ? ' is-wide' : ''}`} data-key-figure={tile.id}>
          <dt className="company-key-figure-label">
            <i className={`feather-${tile.icon} company-key-figure-icon is-${tile.tone}`} aria-hidden="true"></i>
            {tile.label}
          </dt>
          <dd className="company-key-figure-value">{tile.value}</dd>
          {(tile.sub || tile.link) && <dd className="company-key-figure-sub">
            {tile.sub}
            {tile.link && <>{tile.sub ? ' · ' : ''}<Link to={tile.link.to}>{tile.link.label}</Link></>}
          </dd>}
          {tile.createProject && onCreateProject && <dd className="company-key-figure-action">
            <button type="button" className="btn btn-primary" onClick={onCreateProject} data-testid="key-figures-create-project">
              <i className="feather-plus me-1" aria-hidden="true"></i>
              {t('kpiWidget.createProject')}
            </button>
          </dd>}
        </div>
      ))}
    </dl>
  );
}
